import React, { useEffect, useState } from 'react';
import { Drawer, Timeline, Spin, Empty, Button, Alert } from 'antd';
import {
  HistoryOutlined,
  PlusCircleOutlined,
  InboxOutlined,
  UndoOutlined,
  DeleteOutlined,
  EditOutlined,
  UserOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  CameraOutlined,
  VideoCameraOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { getProjectHistory } from '../../../services/projectServices';
import moment from 'moment';

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const m = moment(dateStr);
  const now = moment();
  const diffMinutes = now.diff(m, 'minutes');
  const diffHours = now.diff(m, 'hours');
  const diffDays = now.diff(m, 'days');

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes} mins ago`;
  if (diffHours < 24 && now.isSame(m, 'day')) return `${diffHours} hrs ago`;
  if (now.clone().subtract(1, 'days').isSame(m, 'day') || diffDays === 1) return 'Yesterday';
  if (diffDays > 1) return `${diffDays} days ago`;
  return m.fromNow();
};

const ProjectHistoryDrawer = ({ visible, onClose, projectId, projectName }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [historyList, setHistoryList] = useState([]);
  const [isArchived, setIsArchived] = useState(false);
  const [creationDetails, setCreationDetails] = useState(null);
  const [drawerWidth, setDrawerWidth] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 500 ? '100%' : 460
  );

  useEffect(() => {
    const handleResize = () => {
      setDrawerWidth(window.innerWidth <= 500 ? '100%' : 460);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const fetchHistory = () => {
    if (!projectId) return;
    setLoading(true);
    dispatch(getProjectHistory(projectId))
      .then((res) => {
        setHistoryList(res?.history || []);
        setIsArchived(!!res?.is_archived);
        setCreationDetails(res?.creation_details || null);
      })
      .catch((err) => {
        console.error('Failed to load project history:', err);
        setHistoryList([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (visible && projectId) {
      fetchHistory();
    } else {
      setHistoryList([]);
    }
  }, [visible, projectId]);

  const getEventConfig = (event, description) => {
    const ev = (event || '').toLowerCase();
    const desc = (description || '').toLowerCase();

    if (ev === 'created' || desc.includes('created')) {
      return {
        theme: 'created',
        bg: '#ecfdf5',
        border: '#a7f3d0',
        text: '#059669',
        badgeBg: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
        nodeBg: '#10b981',
        icon: <CheckCircleOutlined />,
        label: 'Project Created',
      };
    }
    if (ev === 'archived' || desc.includes('archived')) {
      return {
        theme: 'archived',
        bg: '#fffbeb',
        border: '#fde68a',
        text: '#d97706',
        badgeBg: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
        nodeBg: '#f59e0b',
        icon: <InboxOutlined />,
        label: 'Project Archived',
      };
    }
    if (ev === 'unarchived' || desc.includes('restored') || desc.includes('unarchived')) {
      return {
        theme: 'restored',
        bg: '#eff6ff',
        border: '#bfdbfe',
        text: '#0284c7',
        badgeBg: 'linear-gradient(135deg, #0284c7 0%, #0038d1 100%)',
        nodeBg: '#0284c7',
        icon: <UndoOutlined />,
        label: 'Restored to Active',
      };
    }
    if (ev === 'deleted' || desc.includes('deleted')) {
      return {
        theme: 'deleted',
        bg: '#fef2f2',
        border: '#fecaca',
        text: '#dc2626',
        badgeBg: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
        nodeBg: '#ef4444',
        icon: <DeleteOutlined />,
        label: 'Deleted',
      };
    }
    if (desc.includes('status')) {
      return {
        theme: 'status',
        bg: '#f0fdf4',
        border: '#bbf7d0',
        text: '#16a34a',
        badgeBg: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
        nodeBg: '#16a34a',
        icon: <EditOutlined />,
        label: 'Status Changed',
      };
    }
    return {
      theme: 'updated',
      bg: '#f5f3ff',
      border: '#ddd6fe',
      text: '#7c3aed',
      badgeBg: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      nodeBg: '#8b5cf6',
      icon: <EditOutlined />,
      label: 'Updated',
    };
  };

  const getChangeList = (item) => {
    const ev = (item.event || '').toLowerCase();
    const desc = (item.description || '').toLowerCase();
    // Do not show field diffs on creation, archiving, restoring, or deletion
    if (ev === 'created' || desc.includes('created') || ev === 'archived' || ev === 'unarchived' || ev === 'deleted') {
      return [];
    }

    if (Array.isArray(item.changes) && item.changes.length > 0) {
      return item.changes;
    }

    const props = item.properties || {};
    const attrs = props.attributes || {};
    const old = props.old || {};

    const labelMap = {
      name: 'Title',
      address: 'Address',
      description: 'Description',
      status: 'Status',
      city: 'City',
      state: 'State',
      country: 'Country',
      postal_code: 'Postal Code',
      street: 'Street',
      house_num: 'House #',
      building_name: 'Building',
      floor: 'Floor',
      landmark: 'Landmark',
    };

    const dirtyKeys = Object.keys(attrs).filter(
      (k) => !['updated_at', 'created_at', 'deleted_at', 'id', 'user_id', 'archived_at'].includes(k)
    );

    const changes = [];
    dirtyKeys.forEach((key) => {
      const oldVal = old[key] !== undefined ? old[key] : null;
      const newVal = attrs[key] !== undefined ? attrs[key] : null;
      if (oldVal !== newVal) {
        changes.push({
          field: key,
          label: labelMap[key] || key.replace(/_/g, ' '),
          old: oldVal,
          new: newVal,
        });
      }
    });

    return changes;
  };

  const getFormattedDescription = (item, changes) => {
    const ev = (item.event || '').toLowerCase();
    const desc = (item.description || '').toLowerCase();

    if (ev === 'created' || desc.includes('created')) {
      return 'Project was created';
    }
    if (ev === 'archived' || desc.includes('archived')) {
      return 'Project was archived';
    }
    if (ev === 'unarchived' || desc.includes('restored') || desc.includes('unarchived')) {
      return 'Project was restored to active';
    }

    if (changes.length === 1) {
      return `Project ${changes[0].label} was updated`;
    }
    if (changes.length > 1) {
      return 'Project details were updated';
    }

    return item.description || 'Project details were updated';
  };

  const getCreationSnapshot = (item) => {
    const details = item.creation_details || creationDetails || {};
    const props = item.properties || {};
    const attrs = props.attributes || {};

    const title = details.title || attrs.name || projectName || '';
    const description = details.description || attrs.description || '';

    // Address parts: Street Address, City, State, Country, Postal Code
    const addressParts = [
      details.street || attrs.street || attrs.address || details.address,
      details.city || attrs.city,
      details.state || attrs.state,
      details.country || attrs.country,
      details.postal_code || attrs.postal_code,
    ].filter(Boolean);

    const fullAddress = details.address || (addressParts.length > 0 ? Array.from(new Set(addressParts)).join(', ') : '');

    const photoCount = details.photo_count !== undefined ? details.photo_count : (attrs.photo_count || 0);
    const videoCount = details.video_count !== undefined ? details.video_count : (attrs.video_count || 0);
    const documentCount = details.document_count !== undefined ? details.document_count : (attrs.document_count || 0);

    return {
      title,
      description,
      address: fullAddress,
      photoCount,
      videoCount,
      documentCount,
    };
  };

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', paddingRight: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #0038d1 0%, #0284c7 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                fontSize: 18,
                boxShadow: '0 3px 10px rgba(0, 56, 209, 0.2)',
              }}
            >
              <HistoryOutlined />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', letterSpacing: '-0.2px', lineHeight: 1.2 }}>
                Project History
              </div>
              <div
                style={{
                  fontSize: 11.5,
                  color: '#475569',
                  fontWeight: 500,
                  marginTop: 2,
                  maxWidth: 'calc(100vw - 220px)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {projectName || `Project #${projectId}`}
              </div>
            </div>
          </div>
          <Button
            size="small"
            icon={<ReloadOutlined spin={loading} />}
            onClick={fetchHistory}
            disabled={loading}
            style={{
              borderRadius: 50,
              fontSize: 11.5,
              fontWeight: 600,
              padding: '0 12px',
              height: 28,
              borderColor: '#cbd5e1',
              color: '#334155',
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
            }}
          >
            Refresh
          </Button>
        </div>
      }
      placement="right"
      width={drawerWidth}
      onClose={onClose}
      visible={visible}
      className="project-history-drawer"
      style={{ maxWidth: '100vw' }}
      bodyStyle={{
        padding: typeof window !== 'undefined' && window.innerWidth <= 500 ? '12px 10px' : '16px 16px',
        background: '#f8fafc',
        overflowX: 'hidden',
      }}
    >
      {isArchived && (
        <Alert
          message={
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontWeight: 700, color: '#92400e', fontSize: 12 }}>Archived Status:</span>
              <span style={{ color: '#b45309', fontSize: 12 }}>This project is currently in Archived Projects.</span>
            </div>
          }
          type="warning"
          showIcon
          icon={<InboxOutlined style={{ fontSize: 16, color: '#d97706' }} />}
          style={{
            marginBottom: 16,
            borderRadius: 10,
            padding: '8px 12px',
            border: '1px solid #fde68a',
            background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
          }}
        />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="default" tip="Loading activity..." />
        </div>
      ) : historyList.length === 0 ? (
        <div
          style={{
            background: '#ffffff',
            borderRadius: 12,
            padding: '40px 20px',
            textAlign: 'center',
            border: '1.5px dashed #e2e8f0',
          }}
        >
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#334155', marginBottom: 2 }}>
                  No History Records Yet
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>
                  Lifecycle events like creation, updates, and archiving will appear here.
                </div>
              </div>
            }
          />
        </div>
      ) : (
        <div className="project-history-timeline-wrap" style={{ paddingLeft: 2, paddingRight: 2 }}>
          <Timeline className="project-activity-timeline">
            {(() => {
              const dedupedHistory = [];
              historyList.forEach((item) => {
                const isDup = dedupedHistory.some((ex) => {
                  const sameDesc = (ex.description || '').trim().toLowerCase() === (item.description || '').trim().toLowerCase();
                  const timeDiff = Math.abs(moment(ex.created_at).diff(moment(item.created_at), 'seconds'));
                  return sameDesc && timeDiff <= 60;
                });
                if (!isDup) {
                  dedupedHistory.push(item);
                }
              });

              return dedupedHistory.map((item, idx) => {
                const cfg = getEventConfig(item.event, item.description);
                const isLatest = idx === 0;
                const isCreated = (item.event || '').toLowerCase() === 'created' || (item.description || '').toLowerCase().includes('created');
                const changes = getChangeList(item);
                const formattedDesc = getFormattedDescription(item, changes);

              return (
                <Timeline.Item
                  key={item.id || idx}
                  dot={
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        background: cfg.nodeBg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 0 0 3px #ffffff, 0 2px 6px ${cfg.nodeBg}55`,
                      }}
                    >
                      {React.cloneElement(cfg.icon, { style: { color: '#ffffff', fontSize: 11 } })}
                    </div>
                  }
                  style={{ paddingBottom: 10 }}
                >
                  <div
                    style={{
                      background: '#ffffff',
                      border: isLatest ? '1.5px solid #cbd5e1' : '1px solid #e2e8f0',
                      borderRadius: 10,
                      padding: '10px 14px',
                      boxShadow: isLatest
                        ? '0 4px 12px -2px rgba(15, 23, 42, 0.06), 0 1px 3px rgba(15, 23, 42, 0.03)'
                        : '0 1px 3px rgba(15, 23, 42, 0.02)',
                      transition: 'all 0.2s ease',
                      position: 'relative',
                    }}
                  >
                    {/* Top Row: Event Badge & Relative Time */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 6,
                        gap: 8,
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          background: cfg.bg,
                          color: cfg.text,
                          border: `1px solid ${cfg.border}`,
                          padding: '1.5px 8px',
                          borderRadius: 50,
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: '0.4px',
                          textTransform: 'uppercase',
                          lineHeight: '14px',
                        }}
                      >
                        {cfg.label}
                      </span>

                      <span
                        style={{
                          fontSize: 11,
                          color: '#64748b',
                          background: '#f8fafc',
                          padding: '1px 6px',
                          borderRadius: 4,
                          fontWeight: 500,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 3,
                          lineHeight: '15px',
                        }}
                        title={item.created_at ? moment(item.created_at).format('LLLL') : ''}
                      >
                        <ClockCircleOutlined style={{ fontSize: 10, color: '#94a3b8' }} />
                        {item.created_at ? formatRelativeTime(item.created_at) : ''}
                      </span>
                    </div>

                    {/* Main Description */}
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#0f172a',
                        lineHeight: 1.35,
                        marginBottom: (changes.length > 0 || isCreated) ? 6 : 8,
                      }}
                    >
                      {formattedDesc}
                    </div>

                    {/* Creation Snapshot: Title, Description, Address (Street, City, State, Country, Postal Code), Photos & Videos at creation */}
                    {isCreated && (() => {
                      const snapshot = getCreationSnapshot(item);
                      return (
                        <div
                          style={{
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 8,
                            padding: '8px 10px',
                            marginTop: 4,
                            marginBottom: 8,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 5,
                          }}
                        >
                          {snapshot.title && (
                            <div style={{ fontSize: 12, color: '#1e293b' }}>
                              <strong style={{ color: '#475569', marginRight: 4 }}>Title:</strong>
                              <span style={{ fontWeight: 600 }}>{snapshot.title}</span>
                            </div>
                          )}

                          {snapshot.description && (
                            <div style={{ fontSize: 11.5, color: '#334155' }}>
                              <strong style={{ color: '#475569', marginRight: 4 }}>Description:</strong>
                              <span>{snapshot.description}</span>
                            </div>
                          )}

                          {snapshot.address && (
                            <div style={{ fontSize: 11.5, color: '#334155', display: 'flex', alignItems: 'flex-start', gap: 5 }}>
                              <EnvironmentOutlined style={{ color: '#0284c7', marginTop: 2, fontSize: 12, flexShrink: 0 }} />
                              <span>
                                <strong style={{ color: '#475569' }}>Address: </strong>
                                {snapshot.address}
                              </span>
                            </div>
                          )}

                          {/* Media count pills at creation time */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2, flexWrap: 'wrap' }}>
                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                fontSize: 10.5,
                                fontWeight: 600,
                                color: '#16a34a',
                                background: '#f0fdf4',
                                padding: '1.5px 7px',
                                borderRadius: 50,
                                border: '1px solid #bbf7d0',
                              }}
                            >
                              <CameraOutlined style={{ fontSize: 11 }} />
                              {snapshot.photoCount} {snapshot.photoCount === 1 ? 'Photo' : 'Photos'}
                            </span>

                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                fontSize: 10.5,
                                fontWeight: 600,
                                color: '#2563eb',
                                background: '#eff6ff',
                                padding: '1.5px 7px',
                                borderRadius: 50,
                                border: '1px solid #bfdbfe',
                              }}
                            >
                              <VideoCameraOutlined style={{ fontSize: 11 }} />
                              {snapshot.videoCount} {snapshot.videoCount === 1 ? 'Video' : 'Videos'}
                            </span>

                            <span
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 4,
                                fontSize: 10.5,
                                fontWeight: 600,
                                color: '#dc2626',
                                background: '#fef2f2',
                                padding: '1.5px 7px',
                                borderRadius: 50,
                                border: '1px solid #fee2e2',
                              }}
                            >
                              <FilePdfOutlined style={{ fontSize: 11 }} />
                              {snapshot.documentCount} {snapshot.documentCount === 1 ? 'Document' : 'Documents'}
                            </span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Before -> After Change Diffs (Only when fields were modified) */}
                    {changes.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 8, marginTop: 2 }}>
                        {changes.map((ch, cIdx) => (
                          <div
                            key={cIdx}
                            style={{
                              background: '#f8fafc',
                              border: '1px solid #e2e8f0',
                              borderRadius: 6,
                              padding: '5px 8px',
                              fontSize: 11.5,
                              lineHeight: 1.4,
                              display: 'flex',
                              alignItems: 'center',
                              flexWrap: 'wrap',
                              gap: 6,
                            }}
                          >
                            <span style={{ fontWeight: 700, color: '#334155' }}>
                              {ch.label}:
                            </span>
                            {ch.old ? (
                              <span style={{ color: '#dc2626', textDecoration: 'line-through', background: '#fee2e2', padding: '0 4px', borderRadius: 3, fontSize: 11 }}>
                                {String(ch.old)}
                              </span>
                            ) : (
                              <span style={{ color: '#94a3b8', fontStyle: 'italic', fontSize: 11 }}>(empty)</span>
                            )}
                            <span style={{ color: '#64748b', fontSize: 11 }}>➔</span>
                            <span style={{ color: '#16a34a', fontWeight: 600, background: '#dcfce7', padding: '0 4px', borderRadius: 3, fontSize: 11 }}>
                              {String(ch.new || '(empty)')}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* User & Date Stamp Footer */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontSize: 11.5,
                        color: '#64748b',
                        borderTop: '1px solid #f1f5f9',
                        paddingTop: 6,
                        marginTop: 2,
                        flexWrap: 'wrap',
                        gap: 6,
                      }}
                    >
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        <div
                          style={{
                            width: 18,
                            height: 18,
                            borderRadius: '50%',
                            background: '#e0f2fe',
                            color: '#0284c7',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 9.5,
                            fontWeight: 700,
                          }}
                        >
                          <UserOutlined />
                        </div>
                        <span style={{ fontWeight: 600, color: '#1e293b' }}>
                          {item.causer_name || 'System'}
                        </span>
                        {item.causer_role && (
                          <span
                            style={{
                              fontSize: 10,
                              color: '#475569',
                              background: '#f1f5f9',
                              padding: '0 5px',
                              borderRadius: 4,
                              fontWeight: 500,
                              lineHeight: '16px',
                            }}
                          >
                            {item.causer_role}
                          </span>
                        )}
                      </div>

                      <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>
                        {item.created_at ? moment(item.created_at).format('dddd, MMM DD, YYYY • hh:mm A') : ''}
                      </span>
                    </div>
                  </div>
                </Timeline.Item>
              );
            });
          })()}
          </Timeline>
        </div>
      )}
    </Drawer>
  );
};

export default ProjectHistoryDrawer;
